import type {
    TypeLanguageFile,
    TypeLanguageRecord,
    TypeLanguageArgs,
    TypeLanguages,
    TypeLanguageState,
    TypeLanguageValue,
    TypeMissingKeyHandler,
} from './types';

import { formatLanguageValue, parseLine } from './utils';

// ----------------------------------------------------------------------------

class LanguageManager {
    readonly #state: TypeLanguageState = {
        defaultLocale: '',
        currentLocale: '',
        supportedLocales: [],
        languages: {},
    };

    #missingKeyHandler: TypeMissingKeyHandler | null = null;

    // ------------------------------------------------------------------------

    constructor(defaultLocale: string, currentLocale: string, supportedLocales: string[], languages: TypeLanguages) {
        this.#state.defaultLocale = defaultLocale;
        this.#state.currentLocale = currentLocale;
        this.#state.supportedLocales = [...supportedLocales];
        this.#state.languages = { ...languages };
    }

    // ------------------------------------------------------------------------

    /*
     * The three shapes a translation can hold, one accessor each, so a caller asking
     * for a title is handed a string instead of the union it then has to narrow. A
     * value of the wrong shape is treated as a missing key and answered with the key
     * itself — the same fallback getLine already uses, and the one thing that is a
     * string whatever the file holds.
     */
    line(line: string, args?: TypeLanguageArgs, locale?: string): string {
        const value = this.getLine(line, args, locale);

        return typeof value === 'string' ? value : line;
    }

    list(line: string, args?: TypeLanguageArgs, locale?: string): string[] {
        const value = this.getLine(line, args, locale);

        if (Array.isArray(value)) {
            return value;
        }

        return [typeof value === 'string' ? value : line];
    }

    // ------------------------------------------------------------------------

    getDefaultLocale(): string {
        return this.#state.defaultLocale;
    }

    getLocale(): string {
        return this.#state.currentLocale;
    }

    setLocale(locale: string): void {
        if (!this.supportsLocale(locale)) {
            throw new Error(`Unsupported locale: ${locale}`);
        }

        this.#state.currentLocale = locale;
    }

    supportsLocale(locale: string): boolean {
        return this.#state.supportedLocales.includes(locale);
    }

    getSupportedLocales(): string[] {
        return [...this.#state.supportedLocales];
    }

    getLanguage(locale?: string): Record<string, TypeLanguageFile | undefined> | undefined {
        return this.#state.languages[locale ?? this.getLocale()];
    }

    getLanguages(): TypeLanguages {
        return this.#state.languages;
    }

    // ------------------------------------------------------------------------

    hasLine(key: string, locale?: string): boolean {
        return this.getLine(key, undefined, locale) !== key;
    }

    addLocale(locale: string, files: Record<string, TypeLanguageFile>): void {
        if (!this.supportsLocale(locale)) {
            this.#state.supportedLocales.push(locale);
        }

        this.#state.languages[locale] = { ...files };
    }

    mergeLocale(locale: string, files: Record<string, TypeLanguageFile>): void {
        const existing = this.#state.languages[locale] ?? {};
        this.#state.languages[locale] = { ...existing, ...files };
    }

    setMissingKeyHandler(handler: TypeMissingKeyHandler | null): void {
        this.#missingKeyHandler = handler;
    }

    withLocale(locale: string): LanguageManager {
        const copy = new LanguageManager(
            this.#state.defaultLocale,
            locale,
            this.#state.supportedLocales,
            this.#state.languages,
        );

        if (this.#missingKeyHandler !== null) {
            copy.setMissingKeyHandler(this.#missingKeyHandler);
        }

        return copy;
    }

    // ------------------------------------------------------------------------

    getLine(line: string, args?: TypeLanguageArgs, locale?: string): TypeLanguageValue {
        const activeLocale = locale ?? this.getLocale();

        if (!line.includes('.')) {
            return formatLanguageValue(line, args);
        }

        const { file, parsedLine } = parseLine(line);

        // pluralization: if args has 'count', try _one / _other variants first
        if (args != null && 'count' in args) {
            const count = args.count as number;
            const pluralSuffix = count === 1 ? '_one' : '_other';
            const pluralLine = `${parsedLine}${pluralSuffix}`;

            let pluralOutput = this.#getTranslationOutput(activeLocale, file, pluralLine);

            if (pluralOutput == null && this.getDefaultLocale() !== activeLocale) {
                pluralOutput = this.#getTranslationOutput(this.getDefaultLocale(), file, pluralLine);
            }

            if (pluralOutput != null) {
                return formatLanguageValue(pluralOutput, args);
            }
        }

        let output = this.#getTranslationOutput(activeLocale, file, parsedLine);

        if (output == null && this.getDefaultLocale() !== activeLocale) {
            output = this.#getTranslationOutput(this.getDefaultLocale(), file, parsedLine);
        }

        if (output == null) {
            if (this.#missingKeyHandler !== null) {
                this.#missingKeyHandler(line, activeLocale);
            }
            output = line;
        }

        return formatLanguageValue(output, args);
    }

    // ------------------------------------------------------------------------

    #getTranslationOutput(locale: string, file: string, parsedLine: string): TypeLanguageValue | null {
        const languageLocale = this.#state.languages[locale];
        if (!languageLocale) {
            return null;
        }

        const languageFile = languageLocale[file];
        if (!languageFile) {
            return null;
        }

        return resolveLanguageValue(languageFile, parsedLine);
    }
}

// ----------------------------------------------------------------------------

function isLanguageRecord(value: unknown): value is TypeLanguageRecord {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function resolveLanguageValue(languageFile: TypeLanguageFile, parsedLine: string): TypeLanguageValue | null {
    const directValue = languageFile[parsedLine];
    if (directValue != null) {
        return directValue;
    }

    const splitted = parsedLine.split('.');
    let current: TypeLanguageRecord | undefined = languageFile;

    for (let index = 0; index < splitted.length; index++) {
        const item = splitted[index];

        if (!isLanguageRecord(current)) {
            return null;
        }

        const output: TypeLanguageValue | undefined = current[item];
        if (typeof output === 'undefined') {
            break;
        }

        if (index === splitted.length - 1) {
            return output;
        }

        if (!isLanguageRecord(output)) {
            break;
        }

        current = output;
    }

    return null;
}

// ----------------------------------------------------------------------------

export default LanguageManager;
